/**
 * FindingCard
 *
 * Durable Scope Intelligence finding. One layout for every state: a bare
 * type-colored icon + label on the left, the resolution status as a pill on
 * the right, the summary, an optional collapsible detail body, source, and the
 * applicable actions. Type is signalled by the card border + icon color.
 *
 * @module @ideoz/ui/components/molecules/FindingCard
 */

import React, { useState } from 'react';
import {
  Gauge,
  Lightbulb,
  TriangleAlert,
  CircleHelp,
  Target,
  Sparkles,
  Check,
  X,
  Search,
  CircleCheck,
  CircleDashed,
  RotateCcw,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../atoms/button';
import { cn } from '../../lib/utils';
import type { FindingAction, FindingStatus, FindingType } from '@ideoz/core';
import type { FindingCardProps } from '../../types/Findings.types';

type ButtonVariant = React.ComponentProps<typeof Button>['variant'];

/**
 * Per-action styling. Humanized labels (per the product mockups) + a clear
 * hierarchy: Confirm is the filled CTA, Investigate is neutral, Dismiss recedes.
 */
const ACTION_CONFIG: Record<
  FindingAction,
  { label: string; icon: LucideIcon; variant: ButtonVariant }
> = {
  confirm: { label: 'Good catch', icon: Check, variant: 'primary' },
  investigate: { label: 'Tell me more', icon: Search, variant: 'secondary-gray' },
  dismiss: { label: 'Skip', icon: X, variant: 'tertiary-gray' },
  validate: { label: 'Looks valid', icon: CircleCheck, variant: 'primary' },
  mark_untested: { label: 'Not tested', icon: CircleDashed, variant: 'tertiary-gray' },
  restore: { label: 'Restore', icon: RotateCcw, variant: 'secondary-gray' },
};

/**
 * Render order for the action row — primary/positive actions first, the ghost
 * "Skip" (dismiss) always last. Actions absent here keep their incoming order
 * ahead of dismiss.
 */
const ACTION_ORDER: FindingAction[] = [
  'confirm',
  'validate',
  'restore',
  'investigate',
  'mark_untested',
  'dismiss',
];

const orderActions = (actions: FindingAction[]): FindingAction[] => {
  const rank = (a: FindingAction) => {
    const i = ACTION_ORDER.indexOf(a);
    return i === -1 ? ACTION_ORDER.length - 1 : i; // unknown before dismiss
  };
  return [...actions].sort((a, b) => rank(a) - rank(b));
};

const ActionButton = ({
  action,
  disabled,
  onClick,
}: {
  action: FindingAction;
  disabled: boolean;
  onClick: () => void;
}) => {
  const cfg = ACTION_CONFIG[action];
  const ActionIcon = cfg?.icon;
  return (
    <Button
      type="button"
      size="xs"
      variant={cfg?.variant ?? 'secondary'}
      disabled={disabled}
      onClick={onClick}
      data-testid={`finding-action-${action}`}
    >
      {ActionIcon && <ActionIcon className="h-3.5 w-3.5" strokeWidth={2.5} />}
      {cfg?.label ?? action}
    </Button>
  );
};


interface TypeTheme {
  label: string;
  icon: LucideIcon;
  /** Card border + faint fill. */
  card: string;
  /** Icon stroke color. */
  iconColor: string;
  /** Type label text color. */
  labelColor: string;
}

/**
 * One theme per finding type — the border color is the primary type signal,
 * echoed by the icon and label. Classes are full literal strings so Tailwind's
 * JIT keeps them.
 */
const TYPE_THEME: Record<FindingType, TypeTheme> = {
  scope_readiness: {
    label: 'Scope readiness',
    icon: Gauge,
    card: 'border-blue-200 bg-blue-25 dark:border-blue-900/40 dark:bg-blue-900/10',
    iconColor: 'text-blue-600 dark:text-blue-400',
    labelColor: 'text-blue-700 dark:text-blue-400',
  },
  key_insight: {
    label: 'Key insight',
    icon: Lightbulb,
    card: 'border-warning-200 bg-warning-25 dark:border-warning-900/40 dark:bg-warning-900/10',
    iconColor: 'text-warning-600 dark:text-warning-400',
    labelColor: 'text-warning-700 dark:text-warning-400',
  },
  contradiction: {
    label: 'Contradiction',
    icon: TriangleAlert,
    card: 'border-error-200 bg-error-25 dark:border-error-900/40 dark:bg-error-900/10',
    iconColor: 'text-error-600 dark:text-error-400',
    labelColor: 'text-error-700 dark:text-error-400',
  },
  assumption: {
    label: 'Assumption',
    icon: CircleHelp,
    // Assumption reads blue in the dark theme (per the mockup), amber-free.
    card: 'border-purple-200 bg-purple-25 dark:border-blue-900/40 dark:bg-blue-900/10',
    iconColor: 'text-purple-600 dark:text-blue-400',
    labelColor: 'text-purple-700 dark:text-blue-400',
  },
  strategy_alignment: {
    label: 'Strategy alignment',
    icon: Target,
    card: 'border-success-200 bg-success-25 dark:border-success-900/40 dark:bg-success-900/10',
    iconColor: 'text-success-600 dark:text-success-400',
    labelColor: 'text-success-700 dark:text-success-400',
  },
  readiness_finding: {
    label: 'Scope finding',
    icon: Sparkles,
    card: 'border-blue-200 bg-blue-25 dark:border-blue-900/40 dark:bg-blue-900/10',
    iconColor: 'text-blue-600 dark:text-blue-400',
    labelColor: 'text-blue-700 dark:text-blue-400',
  },
};

const DEFAULT_THEME: TypeTheme = {
  label: 'Insight',
  icon: Sparkles,
  card: 'border-gray-200 bg-white dark:border-white/10 dark:bg-white/[0.03]',
  iconColor: 'text-gray-500 dark:text-gray-400',
  labelColor: 'text-gray-700 dark:text-gray-300',
};

const READINESS_KIND_LABEL: Record<string, string> = {
  problem_clarity: 'Problem clarity',
  solution_fit: 'Solution fit',
  strength: 'Strength',
};

const SEVERITY_THEME: Record<string, Pick<TypeTheme, 'card' | 'iconColor' | 'labelColor' | 'icon'>> =
  {
    blocker: {
      icon: TriangleAlert,
      card: 'border-error-200 bg-error-25 dark:border-error-900/40 dark:bg-error-900/10',
      iconColor: 'text-error-600 dark:text-error-400',
      labelColor: 'text-error-700 dark:text-error-400',
    },
    warning: {
      icon: TriangleAlert,
      card: 'border-warning-200 bg-warning-25 dark:border-warning-900/40 dark:bg-warning-900/10',
      iconColor: 'text-warning-600 dark:text-warning-400',
      labelColor: 'text-warning-700 dark:text-warning-400',
    },
    insight: {
      icon: Lightbulb,
      card: 'border-success-200 bg-success-25 dark:border-success-900/40 dark:bg-success-900/10',
      iconColor: 'text-success-600 dark:text-success-400',
      labelColor: 'text-success-700 dark:text-success-400',
    },
  };

const themeForFinding = (finding: FindingCardProps['finding']): TypeTheme => {
  if (finding.finding_type === 'readiness_finding') {
    const kind = String(finding.details?.kind ?? '');
    const severity = String(finding.details?.severity ?? '');
    const base = TYPE_THEME.readiness_finding;
    const overlay = SEVERITY_THEME[severity];
    return {
      ...base,
      ...(overlay ?? {}),
      label: READINESS_KIND_LABEL[kind] ?? base.label,
    };
  }
  return TYPE_THEME[finding.finding_type] ?? DEFAULT_THEME;
};

/** Soft status pill — shown on every card so the state reads at a glance. */
const STATUS_PILL: Record<FindingStatus, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  unreviewed: { label: 'New', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  investigating: { label: 'Investigating', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  confirmed: { label: 'Confirmed', cls: 'bg-success-50 text-success-700 border-success-200' },
  validated: { label: 'Validated', cls: 'bg-success-50 text-success-700 border-success-200' },
  resolved: { label: 'Resolved', cls: 'bg-success-50 text-success-700 border-success-200' },
  dismissed: { label: 'Dismissed', cls: 'bg-error-50 text-error-700 border-error-200' },
  untested: { label: 'Untested', cls: 'bg-warning-50 text-warning-700 border-warning-200' },
  expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};

/**
 * FindingCard Component
 */
export const FindingCard: React.FC<FindingCardProps> = ({
  finding,
  onAction,
  isResolving = false,
  className = '',
  testId = 'finding-card',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);

  const theme = themeForFinding(finding);
  const Icon = theme.icon;
  const pill = STATUS_PILL[finding.status];
  // Actions only apply while the finding is unresolved. `resolved_at` is set the
  // moment the user acts (confirm / validate / dismiss / mark untested …), so a
  // resolved card shows no buttons — just its status pill. Fresh findings
  // (untested / active / unreviewed) keep their actions, including the ones that
  // surface inline in the conversation.
  const actions = finding.resolved_at ? [] : (finding.applicable_actions ?? []);

  const explanation = finding.details?.explanation?.trim();
  const contentSummary = finding.details?.content?.summary?.trim();
  const detailBody =
    (explanation && explanation !== finding.content_summary.trim() ? explanation : null) ||
    (contentSummary && contentSummary !== finding.content_summary.trim()
      ? contentSummary
      : null);
  const suggestion = finding.details?.suggestion?.trim() || '';
  const showTellMeMore =
    finding.finding_type === 'readiness_finding' && Boolean(suggestion) && !finding.resolved_at;

  return (
    <article
      data-testid={testId}
      data-finding-id={finding.id}
      data-finding-type={finding.finding_type}
      className={cn('rounded-xl border p-3.5 text-sm', theme.card, className)}
    >
      {/* Header: type (icon + label) ── status pill */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Icon className={cn('h-4 w-4 shrink-0', theme.iconColor)} strokeWidth={2} aria-hidden />
          <span className={cn('truncate text-xs font-medium', theme.labelColor)}>
            {theme.label}
          </span>
        </div>
        {pill && (
          <span
            className={cn(
              'shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
              pill.cls,
              // Dark theme: neutral pill for every status (matches the mockup).
              'dark:bg-white/5 dark:text-gray-300 dark:border-white/10'
            )}
          >
            {pill.label}
          </span>
        )}
      </div>

      <p className="mt-1.5 font-regular leading-snug text-gray-900 dark:text-gray-100">
        {finding.content_summary}
      </p>

      {detailBody && (
        <div className="mt-1.5">
          <p
            className={cn(
              'leading-relaxed text-gray-600 whitespace-pre-wrap dark:text-gray-400',
              !expanded && 'line-clamp-3'
            )}
          >
            {detailBody}
          </p>
          <Button
            variant="link"
            className="p-0"
            size="xs"
            onClick={() => setExpanded((v) => !v)}
            data-testid="finding-body-toggle"
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        </div>
      )}

      {showSuggestion && suggestion && (
        <p
          className="mt-1.5 leading-relaxed text-gray-600 whitespace-pre-wrap dark:text-gray-400"
          data-testid="finding-suggestion"
        >
          {suggestion}
        </p>
      )}

      {finding.source?.label && (
        <p
          className="mt-2 flex items-center gap-1 truncate text-xs text-gray-400 dark:text-gray-500"
          title={finding.source.label}
        >
          <FileText className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          <span className="truncate">Source: {finding.source.label}</span>
        </p>
      )}

      {(actions.length > 0 || showTellMeMore) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {orderActions(actions.filter((a) => a !== 'dismiss')).map((action) => (
            <ActionButton
              key={action}
              action={action}
              disabled={isResolving}
              onClick={() => onAction(finding.id, action)}
            />
          ))}
          {showTellMeMore && (
            <Button
              type="button"
              size="xs"
              variant="secondary-gray"
              onClick={() => setShowSuggestion((v) => !v)}
              data-testid="finding-action-tell-me-more"
            >
              <Search className="h-3.5 w-3.5" strokeWidth={2.5} />
              {showSuggestion ? 'Hide suggestion' : 'Tell me more'}
            </Button>
          )}
          {actions
            .filter((a) => a === 'dismiss')
            .map((action) => (
              <ActionButton
                key={action}
                action={action}
                disabled={isResolving}
                onClick={() => onAction(finding.id, action)}
              />
            ))}
        </div>
      )}
    </article>
  );
};

export default FindingCard;
